import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Cloud,
  ShieldX,
  ShieldCheck,
  ExternalLink,
  Lock,
  Eye,
  Users,
  FileWarning,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  ArrowRight,
  File,
  FileType,
  FolderOpen,
  Globe,
  Copy,
  Check,
  AlertTriangle,
  Server,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

// Cloud Module Data Types
type AccessLevel = "public_write" | "public_read" | "authenticated_read" | "private" | "not_found";

type BucketContentType = "static_hosting" | "static_website" | "sensitive_data" | "unknown";

interface CloudAsset {
  name: string;
  provider: "aws" | "gcp" | "azure" | "digitalocean" | string;
  url?: string;
  access_level?: AccessLevel;
  files_exposed?: number;
  region?: string;
  sample_files?: string[];
  discovery_method?: string;
  content_type?: BucketContentType;
}

export interface CloudModuleData {
  score?: number;
  assets?: CloudAsset[];
  issues?: Array<string | Issue>;
}

export interface CloudModuleViewProps {
  data: CloudModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Provider logos/colors
const PROVIDER_CONFIG: Record<string, { label: string; color: string; chartColor: string }> = {
  aws: { label: "AWS S3", color: "bg-orange-500/10 text-orange-400 border-orange-500/30", chartColor: "#fb923c" },
  gcp: { label: "Google Cloud", color: "bg-blue-500/10 text-blue-400 border-blue-500/30", chartColor: "#60a5fa" },
  azure: { label: "Azure Blob", color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30", chartColor: "#22d3ee" },
  digitalocean: { label: "DigitalOcean", color: "bg-blue-600/10 text-blue-400 border-blue-600/30", chartColor: "#3b82f6" },
  firebase: { label: "Firebase", color: "bg-amber-500/10 text-amber-400 border-amber-500/30", chartColor: "#fbbf24" },
  cloudflare: { label: "Cloudflare R2", color: "bg-orange-600/10 text-orange-400 border-orange-600/30", chartColor: "#ea580c" },
  oracle: { label: "Oracle Cloud", color: "bg-red-500/10 text-red-400 border-red-500/30", chartColor: "#ef4444" },
};

// Pie chart fallback colors
const PIE_COLORS = ["#fb923c", "#60a5fa", "#22d3ee", "#3b82f6", "#fbbf24", "#ea580c", "#818cf8", "#34d399"];

// Provider breakdown pie chart
function ProviderPieChart({ providerCounts }: { providerCounts: Record<string, number> }) {
  const chartData = Object.entries(providerCounts).map(([provider, count]) => ({
    name: PROVIDER_CONFIG[provider]?.label || provider,
    value: count,
    color: PROVIDER_CONFIG[provider]?.chartColor || PIE_COLORS[Object.keys(providerCounts).indexOf(provider) % PIE_COLORS.length],
  }));

  if (chartData.length === 0) return null;

  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={70}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "0.5rem",
            }}
            itemStyle={{ color: "hsl(var(--foreground))" }}
          />
          <Legend
            wrapperStyle={{ fontSize: "11px" }}
            formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// Remediation guidance per access level
const REMEDIATION_GUIDANCE: Record<AccessLevel, { title: string; steps: string[]; severity: "critical" | "high" | "medium" | "info" }> = {
  public_write: {
    title: "Publicly Writable Storage",
    severity: "critical",
    steps: [
      "Immediately revoke public write permissions from the bucket/container",
      "Audit all files for malicious content that may have been uploaded",
      "Review access logs for unauthorized uploads or modifications",
      "Enable versioning to track and recover from unauthorized changes",
      "Implement server-side encryption for all objects",
      "Set up CloudTrail/Activity logging to monitor future access",
    ],
  },
  public_read: {
    title: "Publicly Readable Storage",
    severity: "high",
    steps: [
      "Review all exposed files for sensitive data (PII, credentials, internal docs)",
      "Remove public read access unless explicitly required",
      "If public access is needed, use a CDN with appropriate caching headers",
      "Consider implementing signed URLs for time-limited access",
      "Enable bucket/container logging to monitor access patterns",
    ],
  },
  authenticated_read: {
    title: "Authenticated Access",
    severity: "medium",
    steps: [
      "Verify that only intended users/roles have access",
      "Implement principle of least privilege for IAM policies",
      "Consider using resource-based policies for finer access control",
      "Enable MFA delete for critical buckets",
      "Review and rotate access keys regularly",
    ],
  },
  private: {
    title: "Private Storage",
    severity: "info",
    steps: [
      "Continue to monitor for accidental policy changes",
      "Implement AWS Config rules or Azure Policy for drift detection",
      "Enable versioning and cross-region replication for critical data",
      "Review access logs periodically",
    ],
  },
  not_found: {
    title: "Storage Not Found",
    severity: "info",
    steps: [
      "Bucket/container may have been deleted or doesn't exist",
      "No action required",
    ],
  },
};

// Remediation card component
function RemediationCard({ level, count }: { level: AccessLevel; count: number }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const guidance = REMEDIATION_GUIDANCE[level];

  if (count === 0 || level === "not_found" || level === "private") return null;

  const severityColors = {
    critical: "border-red-500/30 bg-red-500/5",
    high: "border-orange-500/30 bg-orange-500/5",
    medium: "border-amber-500/30 bg-amber-500/5",
    info: "border-slate-500/30 bg-slate-500/5",
  };

  const iconColors = {
    critical: "text-red-400",
    high: "text-orange-400",
    medium: "text-amber-400",
    info: "text-slate-400",
  };

  return (
    <div className={cn("border rounded-lg overflow-hidden", severityColors[guidance.severity])}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Lightbulb className={cn("h-5 w-5", iconColors[guidance.severity])} />
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{guidance.title}</span>
              <Badge variant="outline" className="text-xs">
                {count} asset{count !== 1 ? "s" : ""}
              </Badge>
            </div>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border/50">
          <p className="text-xs text-muted-foreground mt-3 mb-2">Recommended Actions:</p>
          <ol className="space-y-2">
            {guidance.steps.map((step, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                <ArrowRight className="h-3.5 w-3.5 mt-1 flex-shrink-0 text-cyan-400" />
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// Access level config
const ACCESS_CONFIG: Record<AccessLevel, { label: string; icon: typeof Lock; color: string; severity: "critical" | "high" | "medium" | "low" | "info" }> = {
  public_write: {
    label: "Public Write",
    icon: ShieldX,
    color: "bg-red-500/15 text-red-400 border-red-500/40",
    severity: "critical",
  },
  public_read: {
    label: "Public Read",
    icon: Eye,
    color: "bg-orange-500/15 text-orange-400 border-orange-500/40",
    severity: "high",
  },
  authenticated_read: {
    label: "Authenticated",
    icon: Users,
    color: "bg-amber-500/15 text-amber-400 border-amber-500/40",
    severity: "medium",
  },
  private: {
    label: "Private",
    icon: Lock,
    color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
    severity: "info",
  },
  not_found: {
    label: "Not Found",
    icon: ShieldCheck,
    color: "bg-slate-500/15 text-slate-400 border-slate-500/40",
    severity: "info",
  },
};

// Content type config for bucket classification
const CONTENT_TYPE_CONFIG: Record<BucketContentType, { label: string; icon: typeof File; color: string; description: string }> = {
  static_hosting: {
    label: "Static Hosting",
    icon: Server,
    color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40",
    description: "Bucket configured as web server — intentional hosting",
  },
  static_website: {
    label: "Static Website",
    icon: Globe,
    color: "bg-blue-500/15 text-blue-400 border-blue-500/40",
    description: "HTML, CSS, JS, images in public listing",
  },
  sensitive_data: {
    label: "Sensitive Data",
    icon: AlertTriangle,
    color: "bg-red-500/15 text-red-400 border-red-500/40",
    description: "Database dumps, config files, backups, archives, data exports",
  },
  unknown: {
    label: "Unclassified",
    icon: FileType,
    color: "bg-slate-500/15 text-slate-400 border-slate-500/40",
    description: "Mixed or unrecognized content types",
  },
};

// Cloud Asset Card - expandable card with detailed evidence
function CloudAssetCard({ asset }: { asset: CloudAsset }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const config = PROVIDER_CONFIG[asset.provider] || { label: asset.provider, color: "bg-muted" };
  const accessConfig = ACCESS_CONFIG[asset.access_level || "not_found"];
  const AccessIcon = accessConfig.icon;
  const contentConfig = asset.content_type ? CONTENT_TYPE_CONFIG[asset.content_type] : null;
  const ContentIcon = contentConfig?.icon || FileType;

  // Risk level considers both access level AND content type
  const risk = (() => {
    if (asset.access_level === "public_write") return "critical" as const;
    if (asset.access_level === "public_read") {
      if (asset.content_type === "sensitive_data") return "high" as const;
      if (asset.content_type === "unknown" || !asset.content_type) return "medium" as const;
      return "low" as const; // static_hosting or static_website — intentional
    }
    return "none" as const;
  })();

  const isVulnerable = risk === "critical" || risk === "high";
  const isPublic = risk !== "none";
  const filesCount = asset.files_exposed ?? 0;
  const hasSampleFiles = asset.sample_files && asset.sample_files.length > 0;

  const copyUrl = () => {
    if (asset.url) {
      navigator.clipboard.writeText(asset.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className={cn(
      "rounded-lg border overflow-hidden transition-all",
      {
        critical: "border-red-500/40 bg-red-500/5",
        high: "border-red-500/40 bg-red-500/5",
        medium: "border-amber-500/30 bg-amber-500/5",
        low: "border-border bg-card/50",
        none: "border-border bg-card/50",
      }[risk]
    )}>
      {/* Header - always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Provider Icon */}
          <div className={cn(
            "flex h-10 w-10 items-center justify-center rounded-lg shrink-0",
            { critical: "bg-red-500/15", high: "bg-red-500/15", medium: "bg-amber-500/15", low: "bg-muted", none: "bg-muted" }[risk]
          )}>
            <Cloud className={cn("h-5 w-5", { critical: "text-red-400", high: "text-red-400", medium: "text-amber-400", low: "text-muted-foreground", none: "text-muted-foreground" }[risk])} />
          </div>

          {/* Asset Info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <code className="font-mono font-semibold text-sm truncate">{asset.name}</code>
              <Badge variant="outline" className={cn("text-xs shrink-0", config.color)}>
                {config.label}
              </Badge>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
              {asset.region && (
                <span className="flex items-center gap-1">
                  <Globe className="h-3 w-3" />
                  {asset.region}
                </span>
              )}
              {asset.url && (
                <span className="truncate max-w-[200px]">{asset.url}</span>
              )}
            </div>
          </div>
        </div>

        {/* Right side stats */}
        <div className="flex items-center gap-4 shrink-0 ml-4">
          <Badge variant="outline" className={cn("text-xs gap-1", accessConfig.color)}>
            <AccessIcon className="h-3 w-3" />
            {accessConfig.label}
          </Badge>

          {contentConfig && isPublic && (
            <Badge variant="outline" className={cn("text-xs gap-1", contentConfig.color)}>
              <ContentIcon className="h-3 w-3" />
              {contentConfig.label}
            </Badge>
          )}

          {filesCount > 0 && (
            <div className="flex items-center gap-1.5 text-sm">
              <File className={cn("h-4 w-4", isVulnerable ? "text-red-400" : risk === "medium" ? "text-amber-400" : "text-muted-foreground")} />
              <span className={cn("font-mono font-semibold", isVulnerable ? "text-red-400" : risk === "medium" ? "text-amber-400" : "text-muted-foreground")}>{filesCount.toLocaleString()}</span>
              <span className="text-muted-foreground text-xs">files</span>
            </div>
          )}

          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expandable Details */}
      {isExpanded && (
        <div className="border-t border-border p-4 space-y-4 bg-muted/20">
          {/* URL with copy button */}
          {asset.url && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground shrink-0">URL:</span>
              <code className="text-xs font-mono bg-background/50 px-2 py-1 rounded flex-1 truncate">
                {asset.url}
              </code>
              <button
                onClick={(e) => { e.stopPropagation(); copyUrl(); }}
                className="p-1.5 rounded hover:bg-muted transition-colors"
                title="Copy URL"
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-emerald-400" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                )}
              </button>
              <a
                href={asset.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 rounded hover:bg-muted transition-colors"
                title="Open in new tab"
              >
                <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
              </a>
            </div>
          )}

          {/* Content Type Classification */}
          {contentConfig && asset.content_type !== "unknown" && (
            <div className={cn("flex items-center gap-2 px-3 py-2 rounded-lg border", contentConfig.color)}>
              <ContentIcon className="h-4 w-4 shrink-0" />
              <div>
                <span className="text-xs font-medium">{contentConfig.label}</span>
                <span className="text-xs opacity-70 ml-2">{contentConfig.description}</span>
              </div>
            </div>
          )}

          {/* Sample Files */}
          {hasSampleFiles && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <FolderOpen className="h-3.5 w-3.5" />
                <span>Exposed Files (sample):</span>
              </div>
              <div className="bg-background/50 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                <div className="space-y-1">
                  {asset.sample_files!.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <File className="h-3 w-3 text-amber-400 shrink-0" />
                      <code className="font-mono text-foreground/80 truncate">{file}</code>
                    </div>
                  ))}
                </div>
                {filesCount > asset.sample_files!.length && (
                  <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                    ... and {(filesCount - asset.sample_files!.length).toLocaleString()} more files
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Discovery Method */}
          {asset.discovery_method && (
            <div className="text-xs text-muted-foreground">
              <span className="opacity-60">Discovery method:</span>{" "}
              <span className="font-mono">{asset.discovery_method}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      const lower = issue.toLowerCase();
      let severity: Issue["severity"] = "info";

      // Critical: public write access
      if (lower.includes("public write") || lower.includes("publicly writable")) {
        severity = "critical";
      }
      // High: public read access, files exposed, or SEVERE keyword
      else if (
        lower.includes("severe") ||
        lower.includes("public read") ||
        lower.includes("publicly readable") ||
        lower.includes("files exposed")
      ) {
        severity = "high";
      }
      // Medium: other security concerns
      else if (lower.includes("bucket") || lower.includes("storage")) {
        severity = "medium";
      }

      return { id: `cloud-issue-${index}`, severity, message: issue, module: "cloud" };
    }
    return issue;
  });
}

export function CloudModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: CloudModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const assets = data.assets || [];

  // Count by access level
  const accessCounts = assets.reduce((acc, asset) => {
    const level = asset.access_level || "not_found";
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Count by provider
  const providerCounts = assets.reduce((acc, asset) => {
    acc[asset.provider] = (acc[asset.provider] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <ModuleContainer
      moduleName="cloud"
      icon={Cloud}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Risk Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className={cn("bg-card/50", (accessCounts.public_write || 0) > 0 && "border-red-500/30")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <ShieldX className="h-3 w-3 text-red-400" /> Public Write
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold font-mono", (accessCounts.public_write || 0) > 0 ? "text-red-400" : "text-emerald-400")}>
                {accessCounts.public_write || 0}
              </p>
            </CardContent>
          </Card>
          {(() => {
            const riskyPublicRead = assets.filter(a =>
              a.access_level === "public_read" && a.content_type !== "static_hosting" && a.content_type !== "static_website"
            ).length;
            const hasRiskyPublic = riskyPublicRead > 0;
            return (
              <Card className={cn("bg-card/50", hasRiskyPublic && "border-orange-500/30")}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                    <Eye className="h-3 w-3 text-orange-400" /> Public Read
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={cn("text-2xl font-bold font-mono", hasRiskyPublic ? "text-orange-400" : (accessCounts.public_read || 0) > 0 ? "text-blue-400" : "text-emerald-400")}>
                    {accessCounts.public_read || 0}
                  </p>
                </CardContent>
              </Card>
            );
          })()}
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Users className="h-3 w-3 text-amber-400" /> Authenticated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono">{accessCounts.authenticated_read || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                <Lock className="h-3 w-3 text-emerald-400" /> Private
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold font-mono text-emerald-400">{accessCounts.private || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Critical Alert */}
        {(accessCounts.public_write || 0) > 0 && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 animate-pulse">
            <div className="flex items-center gap-3">
              <FileWarning className="h-6 w-6 text-red-400" />
              <div>
                <h4 className="font-semibold text-red-400">Critical: Publicly Writable Storage Found</h4>
                <p className="text-sm text-muted-foreground">
                  {accessCounts.public_write} bucket(s) allow public write access. Immediate action required.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Provider Distribution Chart */}
        {Object.keys(providerCounts).length > 1 && (
          <ModuleSection
            title="Provider Distribution"
            icon={Cloud}
          >
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <ProviderPieChart providerCounts={providerCounts} />
              </div>
            </div>
          </ModuleSection>
        )}

        {/* Provider Badges (for quick reference) */}
        {Object.keys(providerCounts).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(providerCounts).map(([provider, count]) => {
              const config = PROVIDER_CONFIG[provider] || { label: provider, color: "bg-muted" };
              return (
                <Badge key={provider} variant="outline" className={cn("text-xs", config.color)}>
                  {config.label}: {count}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Cloud Assets - Detailed Cards */}
        {assets.length > 0 && (
          <ModuleSection
            title="Cloud Assets"
            icon={Cloud}
            badge={
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">{assets.length} buckets</Badge>
                {(() => {
                  const atRisk = assets.filter(a =>
                    a.access_level === "public_write" ||
                    (a.access_level === "public_read" && (a.content_type === "sensitive_data" || a.content_type === "unknown" || !a.content_type))
                  ).length;
                  const publicHosting = assets.filter(a =>
                    a.access_level === "public_read" && (a.content_type === "static_hosting" || a.content_type === "static_website")
                  ).length;
                  return (
                    <>
                      {atRisk > 0 && (
                        <Badge variant="outline" className="text-xs bg-red-500/15 text-red-400 border-red-500/40">
                          {atRisk} at risk
                        </Badge>
                      )}
                      {publicHosting > 0 && (
                        <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                          {publicHosting} public hosting
                        </Badge>
                      )}
                    </>
                  );
                })()}
              </div>
            }
          >
            <div className="space-y-3">
              {/* Vulnerable assets first */}
              {assets
                .sort((a, b) => {
                  const getSortOrder = (asset: CloudAsset) => {
                    if (asset.access_level === "public_write") return 0;
                    if (asset.access_level === "public_read") {
                      if (asset.content_type === "sensitive_data") return 1;
                      if (asset.content_type === "unknown" || !asset.content_type) return 2;
                      return 3; // static hosting/website — least concerning public
                    }
                    if (asset.access_level === "authenticated_read") return 4;
                    if (asset.access_level === "private") return 5;
                    return 6;
                  };
                  return getSortOrder(a) - getSortOrder(b);
                })
                .map((asset, idx) => (
                  <CloudAssetCard key={asset.url || `${asset.name}-${idx}`} asset={asset} />
                ))}
            </div>
          </ModuleSection>
        )}

        {/* Remediation Guidance */}
        {((accessCounts.public_write || 0) > 0 ||
          (accessCounts.public_read || 0) > 0 ||
          (accessCounts.authenticated_read || 0) > 0) && (
          <ModuleSection
            title="Remediation Guidance"
            icon={Lightbulb}
          >
            <div className="space-y-3">
              <RemediationCard level="public_write" count={accessCounts.public_write || 0} />
              <RemediationCard level="public_read" count={accessCounts.public_read || 0} />
              <RemediationCard level="authenticated_read" count={accessCounts.authenticated_read || 0} />
            </div>
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

CloudModuleView.displayName = "CloudModuleView";
